import { Controller } from "@hotwired/stimulus"

/**
 * ET Reference Select Controller
 * Manages single ET Reference Block equipment selection
 * Equipment type ID: 16 (ET Reference Block)
 *
 * This controller handles:
 *   - Adding equipment (single selection - replaces existing)
 *   - Removing equipment
 *   - Dispatching events for auto-population (et_equipment_controller listens)
 *   - Triggering autosave
 *
 * Usage in HAML:
 *   .card{ data: { controller: "et-reference-select" } }
 *     .card-body
 *       %div{ data: { "et-reference-select-target": "display" } }
 *       = select_tag :et_reference_select, options, data: { "et-reference-select-target": "select" }
 *       %div{ data: { "et-reference-select-target": "hidden" } }
 */
export default class extends Controller {
  static targets = ["display", "select", "hidden"]

  connect() {
    console.log('[et-reference-select] Controller connected')
    this.currentReferenceId = this.getCurrentReferenceId()
    console.log('[et-reference-select] Current reference ID:', this.currentReferenceId)
  }

  /**
   * Get currently selected reference block ID from hidden field
   */
  getCurrentReferenceId() {
    if (!this.hasHiddenTarget) return null

    const hiddenField = this.hiddenTarget.querySelector('input[type="hidden"]')
    return hiddenField ? hiddenField.value : null
  }

  /**
   * Add equipment to the report (single selection - replaces existing)
   */
  async add(event) {
    event.preventDefault()

    if (!this.hasSelectTarget) {
      console.error('[et-reference-select] No select target found')
      return
    }

    const selectElement = this.selectTarget
    const equipmentId = selectElement.value

    if (!equipmentId) {
      alert('Please select an ET Reference Block')
      return
    }

    console.log('[et-reference-select] Adding reference block:', equipmentId)

    try {
      // Fetch equipment data from server
      const response = await fetch(`/equipment/${equipmentId}.json`)
      if (!response.ok) throw new Error('Failed to fetch equipment data')

      const equipmentData = await response.json()
      console.log('[et-reference-select] Equipment data received:', equipmentData)

      // Update display with new equipment
      this.updateDisplay(equipmentId, equipmentData)

      // Update hidden field for form submission
      this.updateHiddenField(equipmentId)

      // Clear selection dropdown
      selectElement.value = ''

      // Reset select2/TomSelect if present
      this.resetSelect(selectElement)

      // Dispatch event for ET auto-populate (existing et_equipment_controller listens for this)
      document.dispatchEvent(new CustomEvent('equipment:added', {
        detail: {
          equipmentId: equipmentId,
          equipmentTypeName: equipmentData.equipment_type_name || 'ET Reference Block',
          equipmentData: equipmentData
        }
      }))
      console.log('[et-reference-select] Dispatched equipment:added event')

      // Trigger autosave
      this.triggerAutosave()

    } catch (error) {
      console.error('[et-reference-select] Error adding reference block:', error)
      alert('Failed to add reference block. Please try again.')
    }
  }

  /**
   * Remove equipment from the report
   */
  async remove(event) {
    event.preventDefault()

    const equipmentId = event.currentTarget.dataset.equipmentId
    console.log('[et-reference-select] Removing reference block:', equipmentId)

    // Dispatch remove event for other controllers
    document.dispatchEvent(new CustomEvent('equipment:removed', {
      detail: {
        equipmentId: equipmentId,
        equipmentTypeName: 'ET Reference Block'
      }
    }))
    console.log('[et-reference-select] Dispatched equipment:removed event')

    // Clear display
    if (this.hasDisplayTarget) {
      this.displayTarget.innerHTML = `
        <div class="text-center text-muted py-3">
          <i class="fa fa-cube mr-2"></i>
          No reference block selected
        </div>
      `
    }

    // Clear hidden field
    if (this.hasHiddenTarget) {
      this.hiddenTarget.innerHTML = ''
    }

    this.currentReferenceId = null

    // Trigger autosave
    this.triggerAutosave()
  }

  /**
   * Update the display table with selected equipment
   */
  updateDisplay(equipmentId, data) {
    if (!this.hasDisplayTarget) return

    this.displayTarget.innerHTML = `
      <div class="table-responsive">
        <table class="table table-sm table-hover mb-0">
          <thead>
            <tr>
              <th>Name</th>
              <th>Type/Model</th>
              <th>Serial No.</th>
              <th class="text-center" style="width: 60px;">Action</th>
            </tr>
          </thead>
          <tbody>
            <tr data-equipment-id="${equipmentId}">
              <td>${this.escapeHtml(data.report_text_or_name || data.name || '')}</td>
              <td>${this.escapeHtml(data.model || '')}</td>
              <td>${this.escapeHtml(data.serial_no || '')}</td>
              <td class="text-center">
                <button class="btn btn-sm btn-link text-danger p-0" type="button"
                        data-action="click->et-reference-select#remove"
                        data-equipment-id="${equipmentId}" title="Remove">
                  <i class="fa fa-trash"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    `
    this.currentReferenceId = equipmentId
  }

  /**
   * Update hidden field for form submission
   */
  updateHiddenField(equipmentId) {
    if (!this.hasHiddenTarget) return

    this.hiddenTarget.innerHTML = `<input type="hidden" name="report[equipment_ids][]" value="${equipmentId}" />`
  }

  /**
   * Reset select dropdown (handles select2 and TomSelect)
   */
  resetSelect(selectElement) {
    // Reset select2 if present
    if (window.jQuery && window.jQuery(selectElement).data('select2')) {
      window.jQuery(selectElement).val('').trigger('change')
    }

    // Reset TomSelect if present
    if (selectElement.tomselect) {
      selectElement.tomselect.clear()
    }
  }

  /**
   * Escape HTML to prevent XSS
   */
  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text || ''
    return div.innerHTML
  }

  /**
   * Trigger autosave via change event
   */
  triggerAutosave() {
    console.log('[et-reference-select] Triggering autosave...')

    // Find and trigger autosave if available
    const autosaveContainer = document.querySelector('[data-controller*="autosave"]')
    if (autosaveContainer) {
      // Dispatch a change event that bubbles up to the autosave controller
      const changeEvent = new Event('change', { bubbles: true })
      this.element.dispatchEvent(changeEvent)
      console.log('[et-reference-select] Dispatched change event for autosave')
    } else {
      console.log('[et-reference-select] No autosave controller found')
    }
  }
}
